import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Outfit } from '../../shared/outfits';
import type { TryOnJob, TryOnReference, TryOnSelection } from '../../shared/tryOn';
import type { WardrobeItem } from '../../shared/wardrobe';
import { listWardrobeItems } from '../lib/items';
import { listOutfits } from '../lib/outfits';
import {
  cancelTryOnJob,
  createTryOnJob,
  createTryOnReference,
  deleteTryOnJob,
  deleteTryOnReference,
  getTryOnJob,
  listTryOnJobs,
  listTryOnReferences,
  retryTryOnJob,
} from '../lib/tryOn';
import { Icon } from './Icon';

function FullscreenPreview({
  url,
  label,
  onClose,
}: {
  url: string;
  label: string;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      aria-label={`Full-screen AI preview for ${label}`}
      aria-modal="true"
      className="tryon-fullscreen"
      role="dialog"
      onClick={onClose}
    >
      <img alt={`AI preview for ${label}`} src={url} onClick={(event) => event.stopPropagation()} />
      <span className="ai-preview-label">AI preview</span>
      <button aria-label="Close full-screen preview" type="button" onClick={onClose}>
        ×
      </button>
    </div>
  );
}

function updateJob(jobs: TryOnJob[], updated: TryOnJob): TryOnJob[] {
  return [updated, ...jobs.filter((job) => job.id !== updated.id)].sort(
    (left, right) => right.updatedAt - left.updatedAt,
  );
}

function ReferenceCreator({
  saving,
  onCreate,
}: {
  saving: boolean;
  onCreate: (name: string, photo: File, consent: boolean) => Promise<void>;
}) {
  const [name, setName] = useState('My try-on photo');
  const [photo, setPhoto] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!photo) return;
    await onCreate(name, photo, consent);
    setPhoto(null);
    setConsent(false);
  }

  return (
    <form className="tryon-reference-form" onSubmit={(event) => void submit(event)}>
      <div>
        <p className="eyebrow">Private reference</p>
        <h2>Add a clear full-body photo</h2>
        <p>
          Use a front-facing photo with comfortable clothing and good light. It stays in private
          storage and can be deleted whenever you want.
        </p>
      </div>
      <label className="field">
        <span>Photo name</span>
        <input
          maxLength={80}
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <label className="tryon-photo-picker">
        <input
          accept="image/jpeg,image/png,image/webp"
          required
          type="file"
          onChange={(event) => setPhoto(event.target.files?.[0] ?? null)}
        />
        <Icon name="add" size={26} />
        <span>{photo?.name ?? 'Choose JPEG, PNG, or WebP'}</span>
        <small>Maximum 12 MB</small>
      </label>
      <label className="tryon-consent">
        <input
          checked={consent}
          required
          type="checkbox"
          onChange={(event) => setConsent(event.target.checked)}
        />
        <span>
          <strong>I choose to save this photo for private virtual try-on.</strong>
          <small>
            It is never used for recommendations, body analysis, training, or sizing predictions.
            The browser also removes embedded photo metadata before upload.
          </small>
        </span>
      </label>
      <button
        className="button button--secondary button--wide"
        disabled={saving || !photo || !consent}
        type="submit"
      >
        {saving ? 'Saving privately…' : 'Save reference photo'}
      </button>
    </form>
  );
}

function SelectionCard({
  active,
  garments,
  name,
  onClick,
}: {
  active: boolean;
  garments: WardrobeItem[];
  name: string;
  onClick: () => void;
}) {
  return (
    <button
      aria-pressed={active}
      className={`tryon-selection-card${active ? ' is-active' : ''}`}
      type="button"
      onClick={onClick}
    >
      <span className="tryon-selection-images">
        {garments.slice(0, 3).map((garment) => (
          <img alt="" key={garment.id} src={garment.photoUrl} />
        ))}
      </span>
      <strong>{name}</strong>
      <small>
        {garments.length} {garments.length === 1 ? 'piece' : 'pieces'}
      </small>
    </button>
  );
}

function JobCard({
  job,
  busy,
  onCancel,
  onDelete,
  onRetry,
  onView,
  confirmingDelete,
}: {
  job: TryOnJob;
  busy: boolean;
  confirmingDelete: boolean;
  onCancel: () => void;
  onDelete: () => void;
  onRetry: () => void;
  onView: () => void;
}) {
  const running = job.status === 'processing' || job.status === 'queued';
  return (
    <article className="tryon-job">
      <div className="tryon-result">
        {job.previewUrl ? (
          <button
            aria-label={`Open the AI preview for ${job.sourceName} in full screen`}
            className="tryon-result-open"
            type="button"
            onClick={onView}
          >
            <img alt={`AI preview for ${job.sourceName}`} src={job.previewUrl} />
          </button>
        ) : (
          <div className="tryon-result-placeholder">
            <Icon name="sparkles" size={34} />
            <strong>
              {running
                ? 'Creating your private preview…'
                : job.status === 'failed'
                  ? 'Preview needs another try'
                  : 'Generation canceled'}
            </strong>
          </div>
        )}
        <span className="ai-preview-label">AI preview</span>
      </div>
      <div className="tryon-job-copy">
        <div>
          <span className={`status-pill is-${job.status}`}>{job.status}</span>
          <h3>{job.sourceName}</h3>
          <p>Using {job.reference.name}</p>
        </div>
        {running ? (
          <div className="tryon-progress" aria-label={`${job.progress}% complete`}>
            <span style={{ width: `${job.progress}%` }} />
          </div>
        ) : null}
        {job.error ? <p className="tryon-error">{job.error}</p> : null}
        <div className="tryon-mini-garments">
          {job.garments.map((garment) => (
            <img alt={garment.name} key={garment.id} src={garment.photoUrl} title={garment.name} />
          ))}
        </div>
        <p className="tryon-disclaimer">
          A creative AI visualization—not an exact fit, size, fabric, or tailoring prediction.
        </p>
        <div className="tryon-job-actions">
          {running ? (
            <button disabled={busy} type="button" onClick={onCancel}>
              Cancel
            </button>
          ) : null}
          {job.status === 'failed' || job.status === 'canceled' ? (
            <button disabled={busy} type="button" onClick={onRetry}>
              Retry
            </button>
          ) : null}
          <button className="is-danger" disabled={busy} type="button" onClick={onDelete}>
            {confirmingDelete ? 'Tap again to confirm' : 'Delete preview'}
          </button>
        </div>
      </div>
    </article>
  );
}

export function TryOnPage() {
  const [references, setReferences] = useState<TryOnReference[]>([]);
  const [garments, setGarments] = useState<WardrobeItem[]>([]);
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [jobs, setJobs] = useState<TryOnJob[]>([]);
  const [referenceId, setReferenceId] = useState('');
  const [selection, setSelection] = useState<TryOnSelection | null>(null);
  const [selectionType, setSelectionType] = useState<'garment' | 'outfit'>('garment');
  const [generationConsent, setGenerationConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingReference, setSavingReference] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState('');
  const [fullscreen, setFullscreen] = useState<{ url: string; label: string } | null>(null);
  const [searchParams] = useSearchParams();
  const requestedOutfitId = searchParams.get('outfit');
  const [notice, setNotice] = useState('');
  const activeItems = useMemo(() => garments.filter((garment) => !garment.archived), [garments]);

  useEffect(() => {
    void Promise.all([listTryOnReferences(), listWardrobeItems(), listOutfits(), listTryOnJobs()])
      .then(([referenceList, garmentList, outfitList, jobList]) => {
        setReferences(referenceList);
        setReferenceId(referenceList[0]?.id ?? '');
        setGarments(garmentList);
        setOutfits(outfitList);
        setJobs(jobList);
        // "See it on me" arrives with the outfit to visualize preselected.
        if (requestedOutfitId && outfitList.some((outfit) => outfit.id === requestedOutfitId)) {
          setSelectionType('outfit');
          setSelection({ type: 'outfit', id: requestedOutfitId });
        }
      })
      .catch((caught: unknown) =>
        setNotice(caught instanceof Error ? caught.message : 'Virtual try-on could not load.'),
      )
      .finally(() => setLoading(false));
    // requestedOutfitId is read once on arrival; later URL edits should not
    // clobber a selection she has already changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const running = jobs.filter((job) => job.status === 'processing' || job.status === 'queued');
    if (!running.length) return;
    const timer = window.setInterval(() => {
      void Promise.all(running.map((job) => getTryOnJob(job.id)))
        .then((updates) =>
          setJobs((current) => updates.reduce((next, job) => updateJob(next, job), current)),
        )
        .catch(() => undefined);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [jobs]);

  async function addReference(name: string, photo: File, consent: boolean) {
    setSavingReference(true);
    setNotice('');
    try {
      const reference = await createTryOnReference(name, photo, consent);
      setReferences((current) => [reference, ...current]);
      setReferenceId(reference.id);
      setNotice('Reference photo saved privately.');
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : 'The reference photo could not save.');
    } finally {
      setSavingReference(false);
    }
  }

  async function removeReference(reference: TryOnReference) {
    // window.confirm is silently blocked in installed/embedded app contexts,
    // so destructive actions use an explicit second tap instead.
    if (confirmingId !== `reference-${reference.id}`) {
      setConfirmingId(`reference-${reference.id}`);
      setNotice(
        `Deleting “${reference.name}” also removes every AI preview made with it. Tap Delete again to confirm.`,
      );
      return;
    }
    setConfirmingId('');
    setBusyId(reference.id);
    setNotice('');
    try {
      await deleteTryOnReference(reference.id);
      const remaining = references.filter((entry) => entry.id !== reference.id);
      setReferences(remaining);
      setReferenceId(remaining[0]?.id ?? '');
      setJobs((current) => current.filter((job) => job.reference.id !== reference.id));
      setNotice('Reference photo and its previews deleted.');
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : 'The reference could not delete.');
    } finally {
      setBusyId(null);
    }
  }

  async function generate() {
    if (!referenceId || !selection || !generationConsent) return;
    setGenerating(true);
    setNotice('');
    try {
      const job = await createTryOnJob(referenceId, selection, crypto.randomUUID());
      setJobs((current) => updateJob(current, job));
      setGenerationConsent(false);
      setNotice(
        job.status === 'failed'
          ? 'The first attempt could not start, but your selection is saved for retry.'
          : 'Your private AI preview has started.',
      );
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : 'The AI preview could not start.');
    } finally {
      setGenerating(false);
    }
  }

  async function act(job: TryOnJob, action: 'cancel' | 'retry' | 'delete') {
    if (action === 'delete' && confirmingId !== `job-${job.id}`) {
      setConfirmingId(`job-${job.id}`);
      setNotice('Tap Delete preview again to permanently remove it.');
      return;
    }
    if (action === 'delete') setConfirmingId('');
    setBusyId(job.id);
    setNotice('');
    try {
      if (action === 'delete') {
        await deleteTryOnJob(job.id);
        setJobs((current) => current.filter((entry) => entry.id !== job.id));
        setNotice('AI preview deleted.');
      } else {
        const updated =
          action === 'cancel' ? await cancelTryOnJob(job.id) : await retryTryOnJob(job.id);
        setJobs((current) => updateJob(current, updated));
        setNotice(action === 'cancel' ? 'Generation canceled.' : 'Generation restarted.');
      }
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : `The preview could not ${action}.`);
    } finally {
      setBusyId(null);
    }
  }

  if (loading)
    return (
      <div className="page">
        <p>Opening your private try-on studio…</p>
      </div>
    );

  return (
    <div className="page tryon-page">
      {fullscreen ? (
        <FullscreenPreview
          label={fullscreen.label}
          url={fullscreen.url}
          onClose={() => setFullscreen(null)}
        />
      ) : null}
      <header className="page-header">
        <div>
          <p className="eyebrow">Private virtual try-on</p>
          <h1>Try-on studio</h1>
          <p>See a creative AI preview of pieces you already own—on mobile or desktop.</p>
        </div>
        <span className="tryon-private-badge">
          <Icon name="check" size={18} />
          Explicit consent
        </span>
      </header>

      <section className="tryon-safety">
        <Icon name="sparkles" size={26} />
        <div>
          <strong>This is visualization, not measurement.</strong>
          <p>
            Results may change drape, texture, details, proportions, or appearance. Never use them
            to decide exact size or fit.
          </p>
        </div>
      </section>
      {notice ? <p className="stylist-notice">{notice}</p> : null}

      <div className="tryon-setup">
        <div className="tryon-reference-panel">
          <ReferenceCreator saving={savingReference} onCreate={addReference} />
          {references.length ? (
            <div className="tryon-reference-list">
              <div>
                <p className="eyebrow">Saved privately</p>
                <h2>Choose your reference</h2>
              </div>
              {references.map((reference) => (
                <article
                  className={`tryon-reference${reference.id === referenceId ? ' is-active' : ''}`}
                  key={reference.id}
                >
                  <button type="button" onClick={() => setReferenceId(reference.id)}>
                    <img alt={reference.name} src={reference.photoUrl} />
                    <span>
                      <strong>{reference.name}</strong>
                      <small>Private reference</small>
                    </span>
                  </button>
                  <button
                    aria-label={
                      confirmingId === `reference-${reference.id}`
                        ? `Confirm deleting ${reference.name}`
                        : `Delete ${reference.name}`
                    }
                    className="tryon-reference-delete"
                    disabled={busyId === reference.id}
                    type="button"
                    onClick={() => void removeReference(reference)}
                  >
                    {confirmingId === `reference-${reference.id}` ? '!' : '×'}
                  </button>
                </article>
              ))}
            </div>
          ) : null}
        </div>

        <section className="tryon-picker">
          <div>
            <p className="eyebrow">Choose from your real closet</p>
            <h2>What would you like to try?</h2>
            <p className="tryon-fidelity-note">
              Previews look most like you with one or two pieces. Every added piece regenerates
              the whole photo again, so faces and poses drift further each step.
            </p>
          </div>
          <div className="tryon-type-tabs">
            <button
              className={selectionType === 'garment' ? 'is-active' : ''}
              type="button"
              onClick={() => {
                setSelectionType('garment');
                setSelection(null);
              }}
            >
              One garment
            </button>
            <button
              className={selectionType === 'outfit' ? 'is-active' : ''}
              type="button"
              onClick={() => {
                setSelectionType('outfit');
                setSelection(null);
              }}
            >
              Saved outfit
            </button>
          </div>
          <div className="tryon-selection-grid">
            {selectionType === 'garment'
              ? activeItems.map((garment) => (
                  <SelectionCard
                    active={selection?.type === 'garment' && selection.id === garment.id}
                    garments={[garment]}
                    key={garment.id}
                    name={garment.name}
                    onClick={() => setSelection({ type: 'garment', id: garment.id })}
                  />
                ))
              : outfits.map((outfit) => (
                  <SelectionCard
                    active={selection?.type === 'outfit' && selection.id === outfit.id}
                    garments={outfit.items.map((item) => item.garment)}
                    key={outfit.id}
                    name={outfit.name}
                    onClick={() => setSelection({ type: 'outfit', id: outfit.id })}
                  />
                ))}
          </div>
          {!activeItems.length && selectionType === 'garment' ? (
            <p className="empty-copy">Add a garment before creating a try-on preview.</p>
          ) : null}
          {!outfits.length && selectionType === 'outfit' ? (
            <p className="empty-copy">Save an outfit with up to four pieces first.</p>
          ) : null}
          <label className="tryon-consent tryon-generation-consent">
            <input
              checked={generationConsent}
              type="checkbox"
              onChange={(event) => setGenerationConsent(event.target.checked)}
            />
            <span>
              <strong>I consent to process this reference for this AI preview.</strong>
              <small>
                The person and garment images are sent as base64—not public URLs. The result returns
                to private storage. Cancel stops this app from polling or saving a result; an
                already-started provider task may finish transiently and expire there.
              </small>
            </span>
          </label>
          <button
            className="button button--primary button--wide"
            disabled={generating || !referenceId || !selection || !generationConsent}
            type="button"
            onClick={() => void generate()}
          >
            {generating ? 'Starting preview…' : 'Create AI preview'}
          </button>
          {selection?.type === 'outfit' ? (
            <small className="tryon-cost-note">
              Saved outfits are applied one piece at a time and support up to four pieces.
            </small>
          ) : null}
        </section>
      </div>

      <section className="tryon-history">
        <div>
          <p className="eyebrow">Private history</p>
          <h2>Your AI previews</h2>
        </div>
        {jobs.length ? (
          <div className="tryon-job-grid">
            {jobs.map((job) => (
              <JobCard
                busy={busyId === job.id}
                confirmingDelete={confirmingId === `job-${job.id}`}
                job={job}
                key={job.id}
                onCancel={() => void act(job, 'cancel')}
                onDelete={() => void act(job, 'delete')}
                onRetry={() => void act(job, 'retry')}
                onView={() =>
                  job.previewUrl
                    ? setFullscreen({ url: job.previewUrl, label: job.sourceName })
                    : undefined
                }
              />
            ))}
          </div>
        ) : (
          <div className="empty-card">
            <Icon name="sparkles" size={30} />
            <h3>No AI previews yet</h3>
            <p>Your first result will appear here and remain private until you delete it.</p>
          </div>
        )}
      </section>
    </div>
  );
}
